import { useEffect } from "react";

/** Events Redo listens for */
export enum RedoListenedDomEvent {
  ReloadCart = "redo:reload-cart", // Fired by the merchant when the they have added/removed items from the cart and so we should re-calculate Redo price
  ReRenderElements = "redo:re-render-elements", // Fired by the merchant when they have re-rendered their cart, and we should check if we should re-place Redo elements
  OpenDebug = "redo:open_debug",
}

export enum ExternalListenedDomEvent {
  RebuyCartChange = "rebuy:cart.change",
  // Upcart API https://aftersell.notion.site/UpCart-Public-API-7a0f6d75cb044871bdb6da5d99cfc755
  UpcartCartLoad = "aftersell-upcart:public-events:cart-loaded",
  UpcartCartUpdated = "aftersell-upcart:public-events:cart-updated",
}

/** Events Redo emits onto document body */
export enum RedoEmittedDomEvent {
  // Cart product changes
  RenderCart = "redo:render-cart", // Fired when we have added/removed redo from the cart and the theme may need to re-render cart elements (usually only relevant for toggles/cart card, not checkout buttons)

  // Elements rendering
  CheckoutButtonsRendered = "redo:checkout-buttons-rendered",
  InfoCardRendered = "redo:info-card-rendered",
  ToggleRendered = "redo:toggle-rendered", // Fired by the actual classic redo toggle only! A button is not a toggle (to a merchant, at least). Deluxe toggle array may fire multiple of these. Use the event DeluxeToggleArrayRendered to get it only once
  DeluxeToggleArrayRendered = "redo:deluxe-toggle-array-rendered",
  CartCardRendered = "redo:cart-card-rendered",
  CartCheckboxRendered = "redo:cart-checkbox-rendered",
  PdpCheckboxRendered = "redo:pdp-checkbox-rendered",
  RemoveAllElements = "redo:remove-all-elements",

  // Shop on site
  ShopOnSiteStarted = "redo:shop-on-site-started",
  ShopOnSiteCompleted = "redo:shop-on-site-completed",

  // Package protection exposure (internal: extension → redo-storefront)
  PackageProtectionExposure = "redo:package-protection-exposure",
  CoverageInfoModalOpened = "redo:coverage-info-modal-opened",
  CoverageWidgetRendered = "redo:coverage-widget-rendered",
  RefreshAutocheck = "redo:refresh-autocheck",

  // Coverage builder host → storefront info-modal controls
  OpenInfoModal = "redo:open-info-modal",
  CloseInfoModal = "redo:close-info-modal",
}

/** Sync a widget's info modal with the coverage builder's open/close events. */
export const useInfoModalOpenEvents = (
  setModalOpen: (open: boolean) => void,
) => {
  useEffect(() => {
    const open = () => setModalOpen(true);
    const close = () => setModalOpen(false);
    document.addEventListener(RedoEmittedDomEvent.OpenInfoModal, open);
    document.addEventListener(RedoEmittedDomEvent.CloseInfoModal, close);
    return () => {
      document.removeEventListener(RedoEmittedDomEvent.OpenInfoModal, open);
      document.removeEventListener(RedoEmittedDomEvent.CloseInfoModal, close);
    };
  }, [setModalOpen]);
};

export const useFireEventOnRender = (eventToEmit: RedoEmittedDomEvent) => {
  useEffect(() => {
    requestAnimationFrame(() => {
      document.body.dispatchEvent(new CustomEvent(eventToEmit));
    });
  }, [eventToEmit]);
};
